import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileText, ImageOff, Loader2, Trash2 } from "lucide-react";
import { useAttachmentUrl } from "@/hooks/useAttachments";
import type { InteractionAttachment } from "@/types";

// ---------------------------------------------------------------------------
// Faixa de anexos — miniaturas clicáveis abaixo do texto da interação
// ---------------------------------------------------------------------------
// Imagem mostra miniatura; PDF mostra um cartão com o nome (não há como gerar
// miniatura de PDF sem renderizador, e não vale trazer um só por isso).
// Clicar numa imagem abre em tamanho grande; clicar num PDF baixa.

function formatarTamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function Miniatura({ anexo, onAbrir }: { anexo: InteractionAttachment; onAbrir: () => void }) {
  const ehImagem = anexo.content_type.startsWith("image/");
  // Só busca o conteúdo de imagem: PDF não vira miniatura, então baixar os bytes
  // aqui seria trazer megabytes pra desenhar um ícone.
  const { url, erro } = useAttachmentUrl(ehImagem ? anexo.id : null);

  if (!ehImagem) {
    return (
      <button
        type="button"
        onClick={onAbrir}
        title={`${anexo.original_name} (${formatarTamanho(anexo.size_bytes)})`}
        className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded border bg-muted/40 p-1 hover:bg-muted"
      >
        <FileText className="h-5 w-5 text-muted-foreground" />
        <span className="w-full truncate text-[10px] leading-tight text-muted-foreground">
          {anexo.original_name}
        </span>
      </button>
    );
  }

  if (erro) {
    return (
      <div
        title="Não foi possível carregar este anexo"
        className="flex h-16 w-16 items-center justify-center rounded border bg-muted/40"
      >
        <ImageOff className="h-5 w-5 text-muted-foreground" />
      </div>
    );
  }

  if (!url) {
    return (
      <div className="flex h-16 w-16 items-center justify-center rounded border bg-muted/40">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <button type="button" onClick={onAbrir} title={anexo.original_name}>
      <img
        src={url}
        alt={anexo.original_name}
        className="h-16 w-16 rounded border object-cover hover:opacity-80"
      />
    </button>
  );
}

function Visualizador({ anexo, onFechar }: { anexo: InteractionAttachment; onFechar: () => void }) {
  const { url, erro } = useAttachmentUrl(anexo.id);
  const ehImagem = anexo.content_type.startsWith("image/");

  return (
    <Dialog open onOpenChange={onFechar}>
      <DialogContent className="max-w-4xl bg-card border-border">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            {anexo.original_name} · {formatarTamanho(anexo.size_bytes)}
          </p>
          {erro && <p className="text-sm text-destructive">Não foi possível carregar o anexo.</p>}
          {!url && !erro && (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          )}
          {url && ehImagem && (
            <img src={url} alt={anexo.original_name} className="max-h-[70vh] w-full object-contain" />
          )}
          {url && !ehImagem && (
            // PDF: link de download em vez de <embed>. O blob: URL abre no
            // leitor nativo do navegador numa aba nova, e o header
            // Content-Disposition: attachment da API já pede download.
            <a href={url} download={anexo.original_name} className="inline-block">
              <Button variant="outline" className="gap-2">
                <FileText className="h-4 w-4" /> Baixar {anexo.original_name}
              </Button>
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface Props {
  anexos: InteractionAttachment[];
  /** Remover anexo é admin-only, igual a editar interação. Sem isto, sem lixeira. */
  onRemover?: (id: string) => void;
}

export function AttachmentStrip({ anexos, onRemover }: Props) {
  const [aberto, setAberto] = useState<InteractionAttachment | null>(null);

  if (!anexos.length) return null;

  return (
    <>
      <div className="flex flex-wrap gap-2 pt-1">
        {anexos.map((a) => (
          <div key={a.id} className="relative">
            <Miniatura anexo={a} onAbrir={() => setAberto(a)} />
            {onRemover && (
              <button
                type="button"
                onClick={() => onRemover(a.id)}
                title="Remover anexo"
                className="absolute -right-1.5 -top-1.5 rounded-full bg-background p-0.5 shadow ring-1 ring-border hover:bg-destructive/10"
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </button>
            )}
          </div>
        ))}
      </div>
      {aberto && <Visualizador anexo={aberto} onFechar={() => setAberto(null)} />}
    </>
  );
}
